import Fade from "@mui/material/Fade";
import { useToggle } from "@react-hookz/web";
import React from "react";
import { SwitchTransition } from "react-transition-group";
import { useSdkContext } from "scmp-sdk/components/contexts";
import { useNewsletterContext } from "scmp-sdk/components/newsletter/newsletter-widget/contexts";
import type { Props as SignupNewsletterProps } from "scmp-sdk/components/newsletter/newsletter-widget/sign-up-newsletter";
import { ActionComponent } from "scmp-sdk/components/newsletter/newsletter-widget/sign-up-newsletter/enums";
import { useAction } from "scmp-sdk/components/newsletter/newsletter-widget/sign-up-newsletter/hooks";
import IconCheckboxChecked from "scmp-sdk/components/newsletter/newsletter-widget/sign-up-newsletter/icon-checkbox-checked.svg";
import IconCheckboxCheckedOff from "scmp-sdk/components/newsletter/newsletter-widget/sign-up-newsletter/icon-checkbox-off.svg";

import { DescriptionDisplay } from "./description-display";
import {
  AgreementCheckboxContainer,
  AgreementContainer,
  AgreementDescription,
  AgreementRow,
  CollapsedContainer,
  Container,
  CTAContainer,
  ExpandedContainer,
  FadeContainer,
  LeftColumn,
  RightColumn,
  TermAndConditionContainer,
  TermAndConditionLink,
  VerticalDivider,
} from "./styles";
import { ThumbnailDisplay } from "./thumbnail-display";
import { TitleDisplay } from "./title-display";

export const SignUpNewsletter: React.FunctionComponent<SignupNewsletterProps> = ({
  isDisableSignupButtonMouseOver = false,
  isShowOverlaySignUp = false,
  onSuccess,
  ...props
}) => {
  const { handleRoutePush, handleTrackGA4 } = useSdkContext();
  const [isAgreedReceiveMarketing, setIsAgreedReceiveMarketing] = useToggle(false);
  const { campaignName } = useNewsletterContext();

  const { actionComponent, handleRenderActionComponent } = useAction({
    initialActionComponent: ActionComponent.CombinedSocialAndEmail,
    isAgreedReceiveMarketing,
    isDisableSignupButtonMouseOver,
    isShowOverlaySignUp,
    onSuccess,
  });

  const isCollapsed =
    actionComponent === ActionComponent.OverlaySignUpButton ||
    actionComponent === ActionComponent.SignUpButton;

  const handleTrackLinkClick = (destinationUrl: string, message: string) => {
    handleTrackGA4({
      action: "click",
      category: "promopopup",
      customized_parameters: {
        cta_type: "nl_signup",
        destination_url: destinationUrl,
        internal_campaign: campaignName ?? "",
        message,
        step: "1",
      },
      subcategory: "",
    });
    void handleRoutePush(destinationUrl);
  };

  const renderTermAndCondition = (tone: "gray" | "light") => (
    <TermAndConditionContainer $tone={tone}>
      By registering, you agree to our{" "}
      <TermAndConditionLink
        href="/terms-conditions"
        onClick={event => {
          event.preventDefault();
          handleTrackLinkClick("/terms-conditions", "T&C");
        }}
      >
        T&amp;C
      </TermAndConditionLink>{" "}
      and{" "}
      <TermAndConditionLink
        href="/privacy-policy"
        onClick={event => {
          event.preventDefault();
          handleTrackLinkClick("/privacy-policy", "Privacy Policy");
        }}
      >
        Privacy Policy
      </TermAndConditionLink>
    </TermAndConditionContainer>
  );

  const renderAgreement = () => (
    <AgreementContainer>
      <AgreementCheckboxContainer onClick={setIsAgreedReceiveMarketing}>
        {isAgreedReceiveMarketing ? <IconCheckboxChecked /> : <IconCheckboxCheckedOff />}
      </AgreementCheckboxContainer>
      <AgreementDescription>
        {`By submitting, you consent to receiving marketing emails from SCMP. If you don't want these, tick here`}
      </AgreementDescription>
    </AgreementContainer>
  );

  const renderCollapsed = () => (
    <CollapsedContainer>
      <ThumbnailDisplay />
      <LeftColumn>
        <TitleDisplay />
        <CTAContainer>{handleRenderActionComponent()}</CTAContainer>
      </LeftColumn>
      <VerticalDivider />
      <RightColumn>
        <DescriptionDisplay />
        {renderTermAndCondition("gray")}
      </RightColumn>
    </CollapsedContainer>
  );

  const renderExpanded = () => (
    <ExpandedContainer>
      {handleRenderActionComponent()}
      <AgreementRow>
        {renderAgreement()}
        {renderTermAndCondition("light")}
      </AgreementRow>
    </ExpandedContainer>
  );

  return (
    <Container {...props}>
      <SwitchTransition mode="out-in">
        <Fade key={String(isCollapsed)} timeout={500}>
          <FadeContainer>{isCollapsed ? renderCollapsed() : renderExpanded()}</FadeContainer>
        </Fade>
      </SwitchTransition>
    </Container>
  );
};

SignUpNewsletter.displayName = "SignUpNewsletter";
